import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { settlementsService } from '@/shared/services/settlementsService';
import { SettlementBatch, SettlementBatchItem } from '@/shared/types/settlements';
import { OrderDetailsModal } from '../components/OrderDetailsModal';
import styles from './SettlementDetailPage.module.css';

export const SettlementDetailPage: React.FC = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [batch, setBatch] = useState<SettlementBatch | null>(null);
    const [items, setItems] = useState<SettlementBatchItem[]>([]);
    const [processing, setProcessing] = useState(false);
    const [selectedOrderInfo, setSelectedOrderInfo] = useState<{
        ids: string[];
        beneficiaryName: string;
    } | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showPaidModal, setShowPaidModal] = useState(false);
    const [payoutReference, setPayoutReference] = useState('');

    // Estado para notificaciones estéticas
    const [notification, setNotification] = useState<{
        show: boolean;
        message: string;
        type: 'success' | 'error' | 'info';
        title: string;
    }>({
        show: false,
        message: '',
        type: 'info',
        title: ''
    });

    useEffect(() => {
        if (batchId) {
            fetchData();
        }
    }, [batchId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await settlementsService.getBatch(batchId!);
            if (response.success) {
                setBatch(response.batch as SettlementBatch);

                // If the service returns items separately, use them
                if (response.items) {
                    setItems(response.items);
                }
                // Fallback if they are inside the batch object
                else if ((response.batch as any).items) {
                    setItems((response.batch as any).items);
                }
            }
        } catch (error) {
            console.error('Error fetching batch details:', error);
            setNotification({
                show: true,
                title: 'Error',
                message: 'Error al cargar los detalles del lote',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        setShowConfirmModal(false);

        try {
            setProcessing(true);
            const res = await settlementsService.confirmBatch(batchId!);
            if (res.success) {
                setNotification({
                    show: true,
                    title: '¡Éxito!',
                    message: 'Lote confirmado exitosamente',
                    type: 'success'
                });
                fetchData();
            }
        } catch (error) {
            setNotification({
                show: true,
                title: 'Error',
                message: 'Error al confirmar el lote',
                type: 'error'
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleMarkPaid = async () => {
        setShowPaidModal(false);

        try {
            setProcessing(true);
            const res = await settlementsService.markPaid(batchId!, {
                payoutMethod: 'BANK_TRANSFER',
                payoutReference: payoutReference
            });
            if (res.success) {
                setNotification({
                    show: true,
                    title: '¡Éxito!',
                    message: 'Lote marcado como pagado',
                    type: 'success'
                });
                fetchData();
            }
        } catch (error) {
            setNotification({
                show: true,
                title: 'Error',
                message: 'Error al marcar como pagado',
                type: 'error'
            });
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return <div className={styles.loadingOverlay}>Cargando detalle del lote...</div>;
    }

    if (!batch) {
        return <div className={styles.container}><h2>Lote no encontrado</h2></div>;
    }

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <div className={styles.headerTitle}>
                    <button className={styles.backButton} onClick={() => navigate('/settlements')}>
                        ← Volver
                    </button>
                    <h1>Lote #{batch.batchId.substring(0, 8)}</h1>
                    <span className={`${styles.statusBadge} ${styles[`status${batch.status}`]}`}>
                        {batch.status}
                    </span>
                </div>

                <div className={styles.actions}>
                    {batch.status === 'DRAFT' && (
                        <button
                            className={styles.btnPrimary}
                            onClick={() => setShowConfirmModal(true)}
                            disabled={processing}
                        >
                            {processing ? 'Confirmando...' : 'Confirmar Lote'}
                        </button>
                    )}
                    {batch.status === 'CONFIRMED' && (
                        <button
                            className={styles.btnSecondary}
                            onClick={() => setShowPaidModal(true)}
                            disabled={processing}
                        >
                            {processing ? 'Procesando...' : 'Marcar como Pagado'}
                        </button>
                    )}
                </div>
            </header>

            <div className={styles.summaryCards}>
                <div className={styles.card}>
                    <span className={styles.cardLabel}>Tipo</span>
                    <span className={styles.cardValue}>
                        {batch.type === 'RESTAURANT' ? 'Comercios' : 'Repartidores'}
                    </span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardLabel}>Pedidos</span>
                    <span className={styles.cardValue}>{batch.ordersCount || 0}</span>
                </div>
                <div className={styles.card}>
                    <span className={styles.cardLabel}>Total a Liquidar</span>
                    <span className={`${styles.cardValue} ${batch.amountTotal! < 0 ? styles.amountNegative : styles.amountPositive}`}>
                        ${batch.amountTotal?.toFixed(2)}
                    </span>
                </div>
                {batch.status === 'PAID' && (
                    <div className={styles.card}>
                        <span className={styles.cardLabel}>Referencia de Pago</span>
                        <span className={styles.cardValue} style={{ fontSize: '1.1rem' }}>
                            {batch.payoutReference || 'Sin referencia'}
                        </span>
                        <small style={{ color: '#666' }}>
                            {batch.paidAt ? new Date(batch.paidAt.seconds * 1000).toLocaleDateString() : ''}
                        </small>
                    </div>
                )}
            </div>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Desglose por Beneficiario</h2>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Sucursal / Repartidor</th>
                            <th>ID</th>
                            <th>Pedidos</th>
                            <th>Monto</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item: SettlementBatchItem) => (
                            <tr key={item.beneficiaryId}>
                                <td style={{ fontWeight: 'bold' }}>{item.beneficiaryName || 'Sin nombre'}</td>
                                <td><small>{item.beneficiaryId}</small></td>
                                <td>{item.ordersCount}</td>
                                <td className={item.amountTotal < 0 ? styles.amountNegative : styles.amountPositive}>
                                    ${item.amountTotal.toFixed(2)}
                                </td>
                                <td>
                                    <button
                                        className={styles.backButton}
                                        onClick={() => setSelectedOrderInfo({
                                            ids: item.orderIds,
                                            beneficiaryName: item.beneficiaryName || (batch.type === 'RESTAURANT' ? 'Comercio' : 'Repartidor')
                                        })}
                                    >
                                        Ver Pedidos
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            {selectedOrderInfo && (
                <OrderDetailsModal
                    orderIds={selectedOrderInfo.ids}
                    batchType={batch.type}
                    beneficiaryName={selectedOrderInfo.beneficiaryName}
                    onClose={() => setSelectedOrderInfo(null)}
                />
            )}

            {showConfirmModal && (
                <div className={styles.confirmModalOverlay}>
                    <div className={styles.confirmModal}>
                        <div className={styles.confirmIcon}>⚠️</div>
                        <h2 className={styles.confirmTitle}>Confirmar Lote de Liquidación</h2>
                        <p className={styles.confirmMessage}>
                            ¿Estás seguro de confirmar este lote? Los pedidos quedarán bloqueados para este pago.
                        </p>
                        <div className={styles.confirmActions}>
                            <button
                                className={`${styles.confirmButton} ${styles.confirmButtonSecondary}`}
                                onClick={() => setShowConfirmModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className={`${styles.confirmButton} ${styles.confirmButtonPrimary}`}
                                onClick={handleConfirm}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPaidModal && (
                <div className={styles.confirmModalOverlay}>
                    <div className={styles.confirmModal}>
                        <div className={styles.confirmIcon}>💰</div>
                        <h2 className={styles.confirmTitle}>Registrar Pago</h2>
                        <p className={styles.confirmMessage}>
                            Estás por marcar el lote como PAGADO. Ingresa la referencia de la transferencia si la tienes.
                        </p>
                        <div className={styles.inputGroup}>
                            <label>Referencia de Pago (Opcional)</label>
                            <input
                                type="text"
                                value={payoutReference}
                                onChange={(e) => setPayoutReference(e.target.value)}
                                placeholder="Ej: Transf. #12345"
                                autoFocus
                            />
                        </div>
                        <div className={styles.confirmActions}>
                            <button
                                className={`${styles.confirmButton} ${styles.confirmButtonSecondary}`}
                                onClick={() => setShowPaidModal(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className={`${styles.confirmButton} ${styles.btnSecondary}`}
                                onClick={handleMarkPaid}
                            >
                                Registrar Pago
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {notification.show && (
                <div className={styles.modalOverlay}>
                    <div className={styles.notificationModal}>
                        <span className={styles.notificationIcon}>
                            {notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : 'ℹ️'}
                        </span>
                        <h2 className={`${styles.notificationTitle} ${notification.type === 'success' ? styles.successTitle :
                            notification.type === 'error' ? styles.errorTitle :
                                styles.infoTitle
                            }`}>
                            {notification.title}
                        </h2>
                        <p className={styles.notificationMessage}>{notification.message}</p>
                        <button
                            className={styles.closeNotificationButton}
                            onClick={() => setNotification({ ...notification, show: false })}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
